import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Zap, LayoutDashboard, LogOut, Package, Search, Sun, Moon, Globe, User, X, Menu } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useSettings } from '../../context/SettingsContext';

const LANGS = [{ code: 'en', label: 'English', flag: '🇬🇧' }, { code: 'fr', label: 'Français', flag: '🇫🇷' }, { code: 'ar', label: 'العربية', flag: '🇸🇦' }];

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const { theme, toggleTheme, lang, setLang, t } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [langMenu, setLangMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fermer le menu mobile au changement de route
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Bloquer le scroll quand menu mobile ouvert
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => { if (searchOpen) searchRef.current?.focus(); }, [searchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
      setMobileOpen(false);
    }
  };

  const navLinks = [
    { path: '/', label: t('nav', 'home') },
    { path: '/products', label: t('nav', 'products') },
    { path: '/contact', label: t('nav', 'contact') },
    ...(isAdmin ? [{ path: '/admin', label: t('nav', 'admin') }] : []),
  ];

  return (
    <>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: scrolled ? 'var(--nav-bg)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        transition: 'all 0.3s ease'
      }}>
        <div className="container" style={{ height: 68, display: 'flex', alignItems: 'center', gap: 12 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginInlineEnd: 24 }}>
            <div style={{ background: 'linear-gradient(135deg, var(--accent), var(--gold))', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={18} color="white" fill="white" />
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em' }}>
              Tech<span style={{ color: 'var(--accent)' }}>Store</span>
            </span>
          </Link>

          {/* Liens desktop */}
          <div className="nav-desktop-links" style={{ display: 'flex', gap: 2, flex: 1 }}>
            {navLinks.map(link => {
              const active = location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path));
              return (
                <Link key={link.path} to={link.path}
                  style={{ padding: '7px 14px', borderRadius: 10, fontSize: 14, fontWeight: active ? 700 : 500, color: active ? 'var(--accent)' : 'var(--text-secondary)', background: active ? 'var(--accent-dim)' : 'transparent', transition: 'var(--transition)' }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; } }}>
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Barre de recherche desktop */}
          {searchOpen ? (
            <form className="nav-desktop-search" onSubmit={handleSearch} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1.5px solid var(--accent)', borderRadius: 14, padding: '0 14px', height: 42 }}>
              <Search size={16} color="var(--accent)" />
              <input ref={searchRef} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t('nav', 'search')} style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 14, fontFamily: 'var(--font-body)' }} />
              <button type="button" onClick={() => setSearchOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}><X size={16} /></button>
            </form>
          ) : (
            <button className="btn btn-ghost btn-icon nav-desktop-search" onClick={() => setSearchOpen(true)}><Search size={18} /></button>
          )}

          {/* Icônes droite */}
          <div className="nav-icons-right" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Thème */}
            <button className="btn btn-ghost btn-icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Langue */}
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-icon" onClick={() => setLangMenu(!langMenu)}><Globe size={18} /></button>
              {langMenu && (
                <div style={{ position: 'absolute', top: '100%', insetInlineEnd: 0, marginTop: 8, background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 16, padding: 8, minWidth: 170, zIndex: 60, boxShadow: 'var(--shadow)' }} onClick={() => setLangMenu(false)}>
                  {LANGS.map(l => (
                    <button key={l.code} onClick={() => setLang(l.code)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', width: '100%', background: lang === l.code ? 'var(--accent-dim)' : 'none', border: 'none', cursor: 'pointer', borderRadius: 10, color: lang === l.code ? 'var(--accent)' : 'var(--text-primary)', fontSize: 14, fontWeight: lang === l.code ? 700 : 400, fontFamily: 'var(--font-body)' }}>
                      <span style={{ fontSize: 18 }}>{l.flag}</span> {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Panier */}
            <Link to="/cart" style={{ position: 'relative', width: 40, height: 40, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'var(--transition)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-dim)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}>
              <ShoppingCart size={18} color="var(--text-secondary)" />
              {count > 0 && <span style={{ position: 'absolute', top: -6, insetInlineEnd: -6, background: 'var(--accent)', color: 'white', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, border: '2px solid var(--bg-primary)' }}>{count > 9 ? '9+' : count}</span>}
            </Link>

            {/* User menu desktop */}
            {user ? (
              <div className="nav-auth-buttons" style={{ position: 'relative' }}>
                <button onClick={() => setUserMenu(!userMenu)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 6px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, cursor: 'pointer' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: 'white' }}>{user.name[0].toUpperCase()}</div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{user.name.split(' ')[0]}</span>
                </button>
                {userMenu && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', insetInlineEnd: 0, background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 18, padding: 8, minWidth: 210, zIndex: 60, boxShadow: 'var(--shadow)' }} onClick={() => setUserMenu(false)}>
                    <div style={{ padding: '10px 14px 14px', borderBottom: '1px solid var(--border)', marginBottom: 8 }}>
                      <div style={{ fontWeight: 700 }}>{user.name}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{user.email}</div>
                    </div>
                    {[{ icon: User, label: t('nav', 'profile'), path: '/profile' }, { icon: Package, label: t('nav', 'orders'), path: '/orders' }, ...(isAdmin ? [{ icon: LayoutDashboard, label: t('nav', 'admin'), path: '/admin' }] : [])].map(item => (
                      <Link key={item.path} to={item.path}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, color: 'var(--text-secondary)', fontSize: 14, transition: 'var(--transition)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
                        <item.icon size={16} />{item.label}
                      </Link>
                    ))}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
                      <button onClick={() => { logout(); navigate('/'); setUserMenu(false); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, color: 'var(--danger)', fontSize: 14, width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(244,63,94,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <LogOut size={16} />{t('nav', 'logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-auth-buttons" style={{ display: 'flex', gap: 8 }}>
                <Link to="/login" className="btn btn-secondary btn-sm">{t('nav', 'login')}</Link>
                <Link to="/register" className="btn btn-primary btn-sm">{t('nav', 'register')}</Link>
              </div>
            )}

            {/* Bouton hamburger mobile */}
            <button
              className="nav-mobile-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
              style={{ display: 'none' }}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── MENU MOBILE ── */}
      <div className={`nav-mobile-menu ${mobileOpen ? 'open' : ''}`}>

        {/* Recherche mobile */}
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 14, padding: '0 14px', height: 48, marginBottom: 8 }}>
          <Search size={16} color="var(--text-muted)" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t('nav', 'search')} style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 15, fontFamily: 'var(--font-body)' }} />
        </form>

        {/* Liens de navigation */}
        {navLinks.map(link => {
          const active = location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path));
          return (
            <Link key={link.path} to={link.path} className={`nav-mobile-link ${active ? 'active' : ''}`}>
              {link.label}
            </Link>
          );
        })}

        <div className="nav-mobile-divider" />

        {/* Utilisateur connecté */}
        {user ? (
          <>
            <div className="nav-mobile-user">
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: 'white', flexShrink: 0 }}>{user.name[0].toUpperCase()}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{user.name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{user.email}</div>
              </div>
            </div>
            <Link to="/profile" className="nav-mobile-link"><User size={18} /> {t('nav', 'profile')}</Link>
            <Link to="/orders" className="nav-mobile-link"><Package size={18} /> {t('nav', 'orders')}</Link>
            {isAdmin && <Link to="/admin" className="nav-mobile-link"><LayoutDashboard size={18} /> {t('nav', 'admin')}</Link>}
            <button onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 14, fontSize: 16, fontWeight: 600, color: 'var(--danger)', background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.2)', cursor: 'pointer', fontFamily: 'var(--font-body)', width: '100%', marginTop: 4 }}>
              <LogOut size={18} /> {t('nav', 'logout')}
            </button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
            <Link to="/login" className="btn btn-secondary btn-full" style={{ justifyContent: 'center' }}>{t('nav', 'login')}</Link>
            <Link to="/register" className="btn btn-primary btn-full" style={{ justifyContent: 'center' }}>{t('nav', 'register')}</Link>
          </div>
        )}
      </div>
    </>
  );
}